import { LocalizationHelpers } from '@tableau/self-service-schedules/lib/src/components/CreateOrEditRecurrenceModelMapper';
import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { Messages } from '../../../codegen/Localize';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ScheduleFrequencyDescriptionFilterFromSessionInfo } from '../../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { L10n } from '../../../ts/L10n/L10n';
import { IScheduleInfo } from '../../../ts/models/IScheduleInfo';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { AppState } from '../../state/AppState';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { IEntityAction } from '../ActionsMenu/EntityActions/IEntityAction';
import { RenameScheduleAction } from '../ActionsMenu/EntityActions/RenameEntityAction';
import { ScheduleRunNowAction } from '../ActionsMenu/EntityActions/ScheduleRunNowAction';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '../Header/SpaceHeader';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';



export interface ScheduleSpaceHeaderProps {
  readonly item: ISpaceItemDetailInfo<'schedule'>;
  readonly displaySchedulesInClientTimezone: boolean;
}

export function ScheduleSpaceHeader(props: ScheduleSpaceHeaderProps): JSX.Element {
  return <ConnectedSpaceHeader
      item={props.item}
      nameRowIcons={
        <React.Fragment>
          { RunNowButton(props.item) }
          { RenameButton(props.item) }
        </React.Fragment>
      }
      metadataRow={
        <React.Fragment>
          <MetaDataRow {...props} />
        </React.Fragment>
      }
  />;
}

export function MetaDataRow(props: ScheduleSpaceHeaderProps) {
  const schedule = props.item;
  const recurrence = IScheduleInfo.toRecurrence(schedule);
  const description = FeatureFlags.adminSelfServiceSchedulesEnabled
    ? LocalizationHelpers.getRecurrenceStringLocalized(recurrence)
    : ScheduleFrequencyDescriptionFilterFromSessionInfo(props.displaySchedulesInClientTimezone)(props.item);
  return (
    <>
      <span className={SpaceHeaderStyles.ScheduleLabel}>
        {Messages.schedule()}
      </span>
      <span className={SpaceHeaderStyles.MetadataInfo}>
        { description }
      </span>
      { props.item.runNextAt &&
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.runNextAt}>
          {Messages.nextRunAt({NEXT_RUN: L10n.localizedDateTime(props.item.runNextAt)})}
        </span>
      }
    </>
  );
}

interface NameRowButtonOwnProps {
  readonly iconPath: string;
  readonly iconActivePath: string;
  readonly action: IEntityAction<IScheduleInfo, {}>;
  readonly item: ISpaceItemDetailInfo<'schedule'>;
}

interface NameRowButtonStateProps {
  readonly isVisible: boolean;
}

interface NameRowButtonDispatchProps {
  readonly onClick: () => void;
}

type NameRowButtonProps = NameRowButtonOwnProps & NameRowButtonStateProps & NameRowButtonDispatchProps;

function NameRowButton(props: NameRowButtonProps) {
  if (!props.isVisible) {
    return null;
  }

  return (
    <ButtonWidget
      buttonType={ButtonType.IconOutline}
      getContent={getDetailButtonContent(props.iconPath, props.iconActivePath)}
      title={props.action.translateName()}
      testId={props.action.name}
      handleClick={props.onClick}
    />
  );
}

const mapStateToProps: MapStateToProps<NameRowButtonStateProps> = (state: AppState, ownProps: NameRowButtonOwnProps) => ({
  isVisible: ownProps.action.isClickable({}, [ownProps.item], state) && ownProps.action.isVisible([ownProps.item], state)
});

const mapDispatchToProps: MapDispatchToProps<NameRowButtonDispatchProps> = (dispatch, ownProps: NameRowButtonOwnProps) => {
  return {
    onClick: () => {
      dispatch(ownProps.action.createReduxAction([ownProps.item]));
    }
  };
};

export const ConnectedNameRowButton: React.ComponentClass<NameRowButtonOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(NameRowButton);

const getDetailButtonContent = (iconPath: string, activePath: string): (state: InteractionState) => JSX.Element => {
  return (state: InteractionState): JSX.Element => {
    const showActiveIcon = state.hover || state.active || state.focus;
    return (
      <div className={SpaceHeaderStyles.InfoDetailContent} >
        <img
          className={SpaceHeaderStyles.InfoDetailImage}
          src={showActiveIcon ? activePath : iconPath }
        />
      </div>
    );
  };
};

const RunNowButton = (item: ISpaceItemDetailInfo<'schedule'>) => {
  const ICON_PATH = imgPath('run_now_small.svg');
  const ACTIVE_ICON_PATH = imgPath('run_now_small_hover.svg');

  return <ConnectedNameRowButton action={ScheduleRunNowAction} iconPath={ICON_PATH} iconActivePath={ACTIVE_ICON_PATH} item={item}/>;
};

const RenameButton = (item: ISpaceItemDetailInfo<'schedule'>) => {
  const ICON_PATH = imgPath('rename_normal.svg');
  const ACTIVE_ICON_PATH = imgPath('rename_hover.svg');

  return <ConnectedNameRowButton action={RenameScheduleAction} iconPath={ICON_PATH} iconActivePath={ACTIVE_ICON_PATH} item={item}/>;
};
